import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function LessonDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    api.get(`/lessons/${id}`).then(({ data }) => setLesson(data));
  }, [id]);

  const markComplete = async () => {
    if (!user) {
      toast("Sign in to track progress.");
      return;
    }
    try {
      await api.post(`/lessons/${id}/complete`);
      setCompleted(true);
      toast.success("Lesson marked complete");
    } catch {
      toast.error("Could not save progress");
    }
  };

  if (!lesson) return <div className="p-10 font-mono-label text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="lesson-detail-page">
      <div className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
          <Link to={`/courses/${lesson.course_id}`} className="font-mono-label text-muted-foreground hover:text-primary" data-testid="back-to-course">
            ← Back to course
          </Link>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-4 font-light leading-none tracking-tighter">{lesson.title}</h1>
          <p className="mt-4 text-muted-foreground max-w-3xl">{lesson.summary}</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Video */}
        <div className="lg:col-span-7">
          <div className="aspect-video bg-black" style={{ borderRadius: 2 }} data-testid="lesson-video">
            <iframe
              src={lesson.video_url}
              title={lesson.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <div className="mt-6 flex items-center justify-between">
            <div className="font-mono-label text-muted-foreground">{lesson.duration_minutes} MINUTES · VIDEO</div>
            <button
              onClick={markComplete}
              disabled={completed}
              className={`${completed ? "btn-outline-mono" : "btn-oxblood"} text-sm inline-flex items-center gap-2`}
              data-testid="mark-complete-button"
            >
              <Check className="w-4 h-4" />
              {completed ? "Completed" : "Mark complete"}
            </button>
          </div>

          {lesson.quiz && (
            <Link
              to={`/quiz/${lesson.quiz.id}`}
              className="mt-10 block porcelain-card p-6 hover:border-primary group"
              data-testid="lesson-quiz-cta"
            >
              <div className="font-mono-label text-primary">Assessment Available</div>
              <div className="flex items-center justify-between mt-2">
                <h3 className="font-serif-display text-2xl leading-none tracking-tight">{lesson.quiz.title} — Quiz</h3>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{lesson.quiz.questions?.length} questions · test your grasp of this lesson.</p>
            </Link>
          )}
        </div>

        {/* Steps */}
        <div className="lg:col-span-5">
          <div className="font-mono-label text-primary mb-6">Wax-Up Steps</div>
          <div className="space-y-0">
            {lesson.steps?.map((s, i) => (
              <button
                key={s.n}
                onClick={() => setActiveStep(i)}
                data-testid={`step-${s.n}`}
                className={`w-full text-left py-5 pl-6 pr-4 border-l-2 transition-colors ${
                  activeStep === i ? "border-primary bg-secondary/40" : "border-border hover:border-foreground"
                }`}
              >
                <div className="flex items-start gap-4">
                  <span className="font-mono text-xl font-light text-muted-foreground">{s.n}</span>
                  <div>
                    <div className="font-serif-display text-xl leading-none">{s.title}</div>
                    {activeStep === i && (
                      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{s.body}</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
